import * as React from 'react';
import { createRootRoute, Link, Outlet } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { drain, installSyncTriggers, subscribe } from '@/lib/outbox';
import { useDisplayMode, useOnlineStatus } from '@/lib/online';

export const Route = createRootRoute({
  component: RootLayout,
});

function NavLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
      )}
      activeProps={{ className: 'text-foreground bg-accent' }}
    >
      {label}
    </Link>
  );
}

function OutboxBadge() {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => subscribe(setCount), []);
  if (count === 0) return null;
  return (
    <span
      className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500/20 px-1.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300"
      title={`${count} pending sync`}
    >
      {count}
    </span>
  );
}

function StatusBanner() {
  const online = useOnlineStatus();
  const [pending, setPending] = React.useState(0);
  const [justSynced, setJustSynced] = React.useState(false);
  const prevPendingRef = React.useRef(0);

  React.useEffect(
    () =>
      subscribe((n) => {
        if (prevPendingRef.current > 0 && n === 0) {
          setJustSynced(true);
          window.setTimeout(() => setJustSynced(false), 3000);
        }
        prevPendingRef.current = n;
        setPending(n);
      }),
    [],
  );

  // When the user comes back online with pending items, kick the drain
  // immediately rather than waiting for installSyncTriggers' debounce.
  React.useEffect(() => {
    if (online && pending > 0) void drain();
  }, [online, pending]);

  if (!online) {
    return (
      <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 pt-[max(0.375rem,env(safe-area-inset-top))] text-center text-xs font-medium text-amber-700 dark:text-amber-300">
        Offline. New entries will save locally and sync when reconnected.
      </div>
    );
  }
  if (justSynced) {
    return (
      <div className="border-b border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 pt-[max(0.375rem,env(safe-area-inset-top))] text-center text-xs font-medium text-emerald-700 dark:text-emerald-300">
        Back online — outbox synced.
      </div>
    );
  }
  return null;
}

function InstallHint() {
  const mode = useDisplayMode();
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem('install-hint-dismissed') === '1') setDismissed(true);
  }, []);

  if (mode === 'standalone' || dismissed) return null;

  // iOS quirk: standalone mode hint differs from Android's beforeinstallprompt.
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isiOS = /iPhone|iPad|iPod/i.test(ua) && !('MSStream' in window);
  if (!isiOS) return null;

  return (
    <div className="mx-4 mt-4 flex items-start gap-3 rounded-md border border-border bg-card p-3 text-sm">
      <div className="flex-1">
        <p className="font-medium">Install to your Home Screen</p>
        <p className="text-xs text-muted-foreground">
          Tap the Share button in Safari, then "Add to Home Screen" — the app opens fullscreen
          and the quick-log tiles work offline.
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          localStorage.setItem('install-hint-dismissed', '1');
          setDismissed(true);
        }}
        className="rounded p-1 text-muted-foreground hover:bg-accent"
        aria-label="Dismiss install hint"
      >
        ×
      </button>
    </div>
  );
}

function RootLayout() {
  React.useEffect(() => {
    installSyncTriggers();
  }, []);

  return (
    <div className="flex min-h-full flex-col">
      <StatusBanner />
      <header className="sticky top-0 z-10 border-b border-border bg-background/80 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="mx-auto flex max-w-screen-md items-center justify-between gap-4 px-4 py-3">
          <Link to="/" className="flex items-center text-base font-semibold tracking-tight">
            EctopicsTracker
            <OutboxBadge />
          </Link>
          <nav className="flex flex-wrap items-center gap-1">
            <NavLink to="/" label="Dashboard" />
            <NavLink to="/log" label="Log" />
            <NavLink to="/rollup" label="Rollup" />
            <NavLink to="/timeline" label="Timeline" />
            <NavLink to="/settings" label="Settings" />
          </nav>
        </div>
      </header>
      <InstallHint />
      <main className="mx-auto w-full max-w-screen-md flex-1 px-4 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <Outlet />
      </main>
    </div>
  );
}
